import React, { useEffect, useRef, useState } from 'react';

const AVATAR_COLORS = [
  'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500',
  'bg-teal-500', 'bg-sky-500', 'bg-indigo-500', 'bg-violet-500', 'bg-fuchsia-500',
];

function getInitials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function Avatar({ name }) {
  return (
    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${getAvatarColor(name || '?')}`}>
      {getInitials(name)}
    </span>
  );
}

function AccessBadge({ employee }) {
  if (employee.isSelf) {
    return (
      <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-[#3a3a46] dark:text-gray-300">
        You
      </span>
    );
  }
  if (employee.accessLevel === 'write') {
    return (
      <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
        Write
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
      Read only
    </span>
  );
}

// Searchable dropdown for switching which employee's timelog calendar is
// being viewed (self or a delegated owner). Native <select> can't render an
// avatar/badge per row or support free-text search, hence a custom listbox.
export default function EmployeeSwitcher({ employees, selectedEmployeeId, onChange, loading = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = employees.find(employee => employee.id === selectedEmployeeId);

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = normalizedQuery
    ? employees.filter(employee => employee.name.toLowerCase().includes(normalizedQuery))
    : employees;

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (employees.length <= 1) return null;

  const handleSelect = (id) => {
    setOpen(false);
    onChange(id);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = filtered[activeIndex];
      if (target) handleSelect(target.id);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => !loading && setOpen(o => !o)}
        disabled={loading}
        className="flex h-10 w-full items-center gap-2 rounded-lg border border-gray-300 bg-white pl-2.5 pr-8 text-sm font-medium text-gray-800 shadow-sm outline-none transition-colors hover:border-gray-400 focus:border-[#ccb778] focus:ring-2 focus:ring-[#ccb778]/20 disabled:cursor-wait disabled:opacity-60 dark:border-[#4a4a52] dark:bg-[#242428] dark:text-gray-100 dark:hover:border-[#666671]"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Employee calendar"
      >
        <Avatar name={selected?.name} />
        <span className="flex-1 truncate text-left">{selected?.name || 'Select employee'}</span>
        <svg className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[260px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-[#36363e] dark:bg-[#242428]">
          <div className="border-b border-gray-100 p-2 dark:border-[#36363e]">
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search employee..."
              className="w-full rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-800 outline-none focus:border-[#ccb778] focus:ring-1 focus:ring-[#ccb778]/30 dark:border-[#4a4a52] dark:bg-[#1c1c20] dark:text-gray-100"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-gray-400 dark:text-gray-500">No employees found</li>
            )}
            {filtered.map((employee, index) => (
              <li key={employee.id} role="option" aria-selected={employee.id === selectedEmployeeId}>
                <button
                  type="button"
                  onClick={() => handleSelect(employee.id)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    index === activeIndex ? 'bg-gray-100 dark:bg-[#3a3a46]' : ''
                  }`}
                >
                  <Avatar name={employee.name} />
                  <span className="flex-1 truncate text-gray-800 dark:text-gray-100">{employee.name}</span>
                  <AccessBadge employee={employee} />
                  {employee.id === selectedEmployeeId && (
                    <svg className="h-4 w-4 shrink-0 text-[#ccb778]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
